import React, { useCallback, useContext, useEffect, useState } from 'react';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '../../../components/bootstrap/Modal';
import Button from '../../../components/bootstrap/Button';
import Badge from '../../../components/bootstrap/Badge';
import Icon from '../../../components/icon/Icon';
import Spinner from '../../../components/bootstrap/Spinner';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Input from '../../../components/bootstrap/forms/Input';
import showNotification from '../../../components/extras/showNotification';
import stateCoverageService, { IStateAssignment } from '../../../services/stateCoverageService';
import { regionalApiService } from '../../../services/regionalService';
import { State } from '../../../types/regionalTypes';
import AuthContext from '../../../contexts/authContext';

interface IStateAssignmentModalProps {
	isOpen: boolean;
	setIsOpen: (open: boolean) => void;
	userId: string;
	userName: string;
}

const StateAssignmentModal: React.FC<IStateAssignmentModalProps> = ({
	isOpen,
	setIsOpen,
	userId,
	userName,
}) => {
	const { user: currentUsername } = useContext(AuthContext);

	const [allStates, setAllStates] = useState<State[]>([]);
	const [assignments, setAssignments] = useState<IStateAssignment[]>([]);
	const [loading, setLoading] = useState(true);
	const [actionLoading, setActionLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [search, setSearch] = useState('');
	const [addNotes, setAddNotes] = useState('');

	const assignedStateIds = new Set(
		assignments.filter((a) => a.isActive).map((a) => a.stateId),
	);

	const fetchData = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const [statesData, assignmentsData] = await Promise.all([
				regionalApiService.getStates(),
				stateCoverageService.getAssignmentsForUser(userId),
			]);
			setAllStates(statesData);
			setAssignments(assignmentsData.assignments || []);
		} catch (err: any) {
			setError(err?.response?.data?.message || err?.message || 'Failed to load data');
		} finally {
			setLoading(false);
		}
	}, [userId]);

	useEffect(() => {
		if (isOpen) {
			fetchData();
			setSearch('');
			setAddNotes('');
		}
	}, [isOpen, fetchData]);

	const handleToggleState = async (state: State) => {
		const isAssigned = assignedStateIds.has(state.stateId);
		setActionLoading(true);
		try {
			if (isAssigned) {
				await stateCoverageService.removeAssignment(userId, state.stateId);
				showNotification('Removed', `${state.stateName} removed from assignments`, 'info');
			} else {
				await stateCoverageService.assignUserToState({
					userId,
					stateId: state.stateId,
					assignedBy: currentUsername,
					notes: addNotes.trim() || undefined,
				});
				showNotification('Assigned', `${state.stateName} assigned successfully`, 'success');
			}
			await fetchData();
		} catch (err: any) {
			showNotification(
				'Error',
				err?.response?.data?.message || 'Operation failed',
				'danger',
			);
		} finally {
			setActionLoading(false);
		}
	};

	const handleRemoveAll = async () => {
		if (!window.confirm(`Remove all state assignments for ${userName}?`)) return;
		setActionLoading(true);
		try {
			const res = await stateCoverageService.removeAllAssignments(userId);
			showNotification(
				'Cleared',
				`Removed ${res.removedCount} assignment(s)`,
				'info',
			);
			await fetchData();
		} catch (err: any) {
			showNotification('Error', err?.response?.data?.message || 'Failed to remove assignments', 'danger');
		} finally {
			setActionLoading(false);
		}
	};

	const filteredStates = allStates.filter(
		(s) =>
			s.stateName.toLowerCase().includes(search.toLowerCase()) ||
			s.stateCode.toLowerCase().includes(search.toLowerCase()),
	);

	const activeAssignments = assignments.filter((a) => a.isActive);

	return (
		<Modal
			isOpen={isOpen}
			setIsOpen={setIsOpen}
			size='xl'
			titleId='state-assignment-modal'
			isCentered
			isStaticBackdrop
			isScrollable>
			<ModalHeader setIsOpen={setIsOpen}>
				<ModalTitle id='state-assignment-modal'>
					<Icon icon='Map' className='me-2 text-primary' />
					State Assignments — {userName}
				</ModalTitle>
			</ModalHeader>

			<ModalBody>
				{error && (
					<div className='alert alert-danger mb-3' role='alert'>
						<Icon icon='Error' className='me-2' />
						{error}
					</div>
				)}

				{loading ? (
					<div className='d-flex justify-content-center align-items-center py-5'>
						<Spinner color='primary' />
						<span className='ms-3 text-muted'>Loading state data...</span>
					</div>
				) : (
					<>
						{/* Current Assignments */}
						<div className='mb-4'>
							<div className='d-flex align-items-center justify-content-between mb-2'>
								<h6 className='fw-bold mb-0'>
									<Icon icon='CheckCircle' color='success' className='me-1' />
									Currently Assigned States
									<Badge color='success' className='ms-2'>
										{activeAssignments.length}
									</Badge>
								</h6>
								{activeAssignments.length > 0 && (
									<Button
										color='danger'
										isOutline
										size='sm'
										icon='DeleteSweep'
										onClick={handleRemoveAll}
										isDisable={actionLoading}>
										Remove All
									</Button>
								)}
							</div>
							{activeAssignments.length === 0 ? (
								<div className='text-muted small border rounded p-3 bg-light'>
									<Icon icon='Info' className='me-1' />
									No states assigned. This user can access all states.
								</div>
							) : (
								<div className='d-flex flex-wrap gap-2 border rounded p-3 bg-light'>
									{activeAssignments.map((a) => {
										const stateInfo = allStates.find((s) => s.stateId === a.stateId);
										return (
											<span
												key={a.assignmentId}
												className='badge bg-success d-flex align-items-center gap-1 px-2 py-2'
												style={{ fontSize: '0.8rem' }}>
												{stateInfo?.stateName || `State ${a.stateId}`}
												<button
													type='button'
													className='btn-close btn-close-white ms-1'
													style={{ fontSize: '0.6rem' }}
													onClick={() =>
														stateInfo && handleToggleState(stateInfo)
													}
													disabled={actionLoading}
													aria-label='Remove'
												/>
											</span>
										);
									})}
								</div>
							)}
						</div>

						{/* Notes for new assignments */}
						<div className='mb-3'>
							<FormGroup id='assignment-notes' label='Notes for new assignments (optional)'>
								<Input
									type='text'
									placeholder='e.g. Assigned for AP regional coverage'
									value={addNotes}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
										setAddNotes(e.target.value)
									}
									disabled={actionLoading}
								/>
							</FormGroup>
						</div>

						{/* State search and grid */}
						<div className='mb-3'>
							<FormGroup id='state-search' label='Search & toggle states'>
								<Input
									type='search'
									placeholder='Search by state name or code...'
									value={search}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
										setSearch(e.target.value)
									}
								/>
							</FormGroup>
						</div>

						<div className='row g-2' style={{ maxHeight: '400px', overflowY: 'auto' }}>
							{filteredStates.map((state) => {
								const assigned = assignedStateIds.has(state.stateId);
								return (
									<div key={state.stateId} className='col-6 col-md-4 col-lg-3'>
										<button
											type='button'
											className={`btn w-100 btn-sm d-flex align-items-center gap-2 ${
												assigned
													? 'btn-success'
													: 'btn-outline-secondary'
											}`}
											onClick={() => handleToggleState(state)}
											disabled={actionLoading}
											style={{ minHeight: '48px', fontSize: '0.8rem' }}>
											<Icon
												icon={assigned ? 'CheckBox' : 'CheckBoxOutlineBlank'}
												size='sm'
											/>
											<div className='text-start'>
												<div className='fw-semibold'>{state.stateName}</div>
												<div
													className={
														assigned ? 'text-white-50' : 'text-muted'
													}
													style={{ fontSize: '0.7rem' }}>
													{state.stateCode}
												</div>
											</div>
										</button>
									</div>
								);
							})}
							{filteredStates.length === 0 && (
								<div className='col-12 text-center text-muted py-4'>
									No states match your search.
								</div>
							)}
						</div>
					</>
				)}
			</ModalBody>

			<ModalFooter>
				<div className='me-auto text-muted small'>
					<Icon icon='Info' className='me-1' />
					Click a state to toggle assignment. Empty = access to all states.
				</div>
				<Button color='secondary' isOutline onClick={() => setIsOpen(false)}>
					Close
				</Button>
			</ModalFooter>
		</Modal>
	);
};

export default StateAssignmentModal;
